import { useEffect } from "react";
import { logger } from "../../utils/logger";

interface PlaceResult {
  address: string;
  addressParts: {
    house?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
}

interface GooglePlacesOptions {
  componentRestrictions?: {
    country: string | string[];
  };
  types?: string[];
}

export const useGooglePlaces = (
  onPlaceSelect: (place: PlaceResult) => void,
  options: GooglePlacesOptions = {
    componentRestrictions: { country: ['us', 'ca'] },
    types: ['address'],
  },
  onInvalidAddress?: (message: string, address?: string) => void,
): void => {
  useEffect(() => {
    const initAutocomplete = async () => {
      const input = document.getElementById(
        "address-input",
      ) as HTMLInputElement;
      if (!input) {
        logger.warn("Address input element not found");
        return;
      }

      try {
        const { Autocomplete } = (await google.maps.importLibrary(
          "places",
        )) as google.maps.PlacesLibrary;
        const autocomplete = new Autocomplete(input, options);

        autocomplete.addListener("place_changed", () => {
          try {
            const place = autocomplete.getPlace();
            if (!place.address_components) {
              logger.warn("No address components found");
              return;
            }

            const addressParts = {
              house: "",
              street: "",
              city: "",
              state: "",
              zip: "",
              country: "",
            };

            // Parse address components
            place.address_components.forEach((component) => {
              const types = component.types;

              if (types.includes("street_number")) {
                addressParts.house = component.long_name;
              } else if (types.includes("route")) {
                addressParts.street = component.long_name;
              } else if (types.includes("locality")) {
                addressParts.city = component.long_name;
              } else if (types.includes("administrative_area_level_1")) {
                addressParts.state = component.short_name;
              } else if (types.includes("postal_code")) {
                addressParts.zip = component.long_name;
              } else if (types.includes("country")) {
                addressParts.country = component.short_name;
              }
            });

            // Validate that address has a house number (street_number)
            if (!addressParts.house) {
              logger.warn("Selected address is missing a house number");
              onInvalidAddress?.(
                "Please enter a complete street address with a house number",
                place.formatted_address || "",
              );
              return;
            }

            onPlaceSelect({
              address: place.formatted_address || "",
              addressParts,
            });
          } catch (error) {
            logger.error("Error processing place selection", { error });
          }
        });
      } catch (error) {
        logger.error("Error initializing autocomplete", { error });
      }
    };

    // Initialize Places library
    const initializeGooglePlaces = async () => {
      try {
        await initAutocomplete();
      } catch (error) {
        logger.error("Failed to initialize Google Places", { error });
      }
    };

    if (window.google?.maps) {
      void initializeGooglePlaces();
    } else {
      window.initGoogleMaps = () => {
        window.googleMapsLoaded = true;
        void initializeGooglePlaces();
      };
    }

    // Cleanup
    return () => {
      // No cleanup needed
    };
  }, [onPlaceSelect, options, onInvalidAddress]);
};

// Reference the Google Maps types
/// <reference path="../types/google-maps.d.ts" />

declare global {
  interface Window {
    google?: typeof google;
    googleMapsLoaded?: boolean;
    initGoogleMaps?: () => void;
  }
}

// Add PlacesLibrary type
declare namespace google.maps {
  interface PlacesLibrary {
    Autocomplete: typeof google.maps.places.Autocomplete;
  }
}

export type { PlaceResult };
